import { useEffect, useState } from "react";
import { Plus, Pencil, Search, Wrench } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, fmtDate, formatApiError } from "@/lib/api";
import { TOOL_STATUSES } from "@/lib/constants";
import { PageHeader, ConfirmDelete, Field, PhotoUpload, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const blank = () => ({ name: "", brand: "", model: "", serial: "", purchase_date: "", status: "disponible", assigned_to: "", photo_path: "", notes: "" });

function ToolDialog({ item, onClose, onSaved }) {
  const [f, setF] = useState(item);
  useEffect(() => { setF(item); }, [item]);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    try {
      if (f.id) await api.put(`/tools/${f.id}`, f); else await api.post("/tools", f);
      toast.success("Herramienta guardada");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{f.id ? "Editar herramienta" : "Nueva herramienta"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="tool-form">
          <div className="sm:col-span-2"><PhotoUpload value={f.photo_path} onChange={set("photo_path")} testId="tool-photo" /></div>
          <Field label="Nombre *" className="sm:col-span-2"><Input required value={f.name} onChange={set("name")} placeholder="Ej. Sierra circular" data-testid="tool-name-input" /></Field>
          <Field label="Marca"><Input value={f.brand} onChange={set("brand")} data-testid="tool-brand-input" /></Field>
          <Field label="Modelo"><Input value={f.model} onChange={set("model")} data-testid="tool-model-input" /></Field>
          <Field label="No. de serie"><Input value={f.serial} onChange={set("serial")} data-testid="tool-serial-input" /></Field>
          <Field label="Fecha de compra"><Input type="date" value={f.purchase_date} onChange={set("purchase_date")} data-testid="tool-purchase-input" /></Field>
          <Field label="Estado">
            <Select value={f.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="tool-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TOOL_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Asignada a"><Input value={f.assigned_to} onChange={set("assigned_to")} placeholder="Persona o trabajo" data-testid="tool-assigned-input" /></Field>
          <Field label="Notas" className="sm:col-span-2"><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="tool-notes-input" /></Field>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" className="bg-amber-700 hover:bg-amber-800" data-testid="tool-save-button">Guardar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ToolCard({ t, onEdit, onStatus, onDelete }) {
  return (
    <div className="rounded-xl border bg-white overflow-hidden fade-up" data-testid={`tool-card-${t.id}`}>
      <div className="relative aspect-[4/3] bg-stone-100">
        {t.photo_path ? <img src={fileUrl(t.photo_path)} alt={t.name} className="h-full w-full object-cover" /> :
          <div className="h-full grid place-items-center text-stone-300"><Wrench className="h-10 w-10" /></div>}
        <div className="absolute top-2 left-2"><StatusBadge status={t.status} testId={`tool-status-badge-${t.id}`} /></div>
      </div>
      <div className="p-4 space-y-2">
        <div>
          <p className="font-bold leading-tight">{t.name}</p>
          <p className="text-xs text-stone-500">{[t.brand, t.model].filter(Boolean).join(" · ") || "—"}</p>
          <p className="text-[11px] text-stone-400 font-mono">S/N {t.serial || "—"} · Compra {fmtDate(t.purchase_date)}</p>
          {t.assigned_to && <p className="text-xs mt-1">Asignada a: <b>{t.assigned_to}</b></p>}
        </div>
        <div className="flex items-center gap-1">
          <Select value={t.status} onValueChange={(v) => onStatus(t, v)}>
            <SelectTrigger className="h-8 text-xs flex-1" data-testid={`tool-quick-status-${t.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{TOOL_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="ghost" size="icon" onClick={() => onEdit(t)} data-testid={`tool-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
          <ConfirmDelete onConfirm={() => onDelete(t)} testId={`tool-delete-${t.id}`} label="esta herramienta" />
        </div>
      </div>
    </div>
  );
}

export default function Tools() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/tools").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onStatus = async (t, status) => {
    const { data } = await api.patch(`/tools/${t.id}/status`, { status, assigned_to: status === "en_uso" ? t.assigned_to : "" });
    setItems((list) => list.map((x) => (x.id === t.id ? data : x)));
  };
  const onDelete = async (t) => { await api.delete(`/tools/${t.id}`); toast.success("Herramienta eliminada"); load(); };

  const shown = items.filter((t) => (filter === "all" || t.status === filter) && `${t.name} ${t.brand} ${t.model} ${t.serial}`.toLowerCase().includes(q.toLowerCase()));
  const count = (s) => items.filter((t) => t.status === s).length;

  return (
    <div>
      <PageHeader title="Stock de herramientas" subtitle={`${items.length} herramientas · ${count("disponible")} disponibles · ${count("reparacion")} en reparación`}>
        <Button className="bg-amber-700 hover:bg-amber-800" onClick={() => setEditing(blank())} data-testid="new-tool-button"><Plus className="h-4 w-4 mr-1" />Nueva herramienta</Button>
      </PageHeader>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
          <Input className="pl-9 bg-white" placeholder="Buscar herramienta, marca o serie" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tool-search-input" />
        </div>
        <div className="flex flex-wrap gap-2">
          {[{ v: "all", l: "Todas" }, ...TOOL_STATUSES].map((s) => (
            <button key={s.v} onClick={() => setFilter(s.v)} data-testid={`tools-filter-${s.v}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${filter === s.v ? "bg-[#1E1B18] text-amber-400 border-[#1E1B18]" : "bg-white hover:bg-stone-100"}`}>
              {s.l}
            </button>
          ))}
        </div>
      </div>
      {shown.length === 0 && <p className="text-center text-stone-400 py-16">No hay herramientas para mostrar</p>}
      <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {shown.map((t) => <ToolCard key={t.id} t={t} onEdit={setEditing} onStatus={onStatus} onDelete={onDelete} />)}
      </div>
      <ToolDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
